/**
 * AI caption client (V2) — talks to the `POST /v2-caption` lambda (Claude Haiku).
 *
 * Used by the finished-video caption composer: autogenerate a viral caption from
 * the video's plan card, then refine it with a free-text instruction. The caption
 * comes back in the plan's own language.
 */
import { API_CONFIG } from '@/api/config';
import type { V2VideoPlan } from '@/api/v2Service';
import { getIdToken } from '@/auth/firebase';
import { endSessionForReauth, isAuthRequiredError } from '@/api/banInterceptor';

const getUserId = (): string => localStorage.getItem('vivideo_user_id') || '';

// JSON headers plus the caller's Bearer credential (backend verifies the owner).
const authHeaders = async (): Promise<Record<string, string>> => {
  const token = await getIdToken();
  return token
    ? { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
    : { 'Content-Type': 'application/json' };
};

export const generateCaption = async (opts: {
  plan?: V2VideoPlan | null;
  /** The video's title/prompt — the context that ALWAYS exists (manual videos have
   *  no plan card, so this is what feeds the model for them). */
  videoTitle?: string;
  /** Free-text tweak ("make it funnier", "add hashtags"). Omit for a fresh write. */
  instruction?: string;
  /** The caption currently in the box — refined rather than replaced when present. */
  currentCaption?: string;
  /** Also generate a viral YouTube video title in the same call (composer shows the
   *  title field when a title platform like YouTube is connected). */
  wantTitle?: boolean;
  /** The title currently in the box — refined rather than replaced when present. */
  currentTitle?: string;
}): Promise<{ caption: string; title?: string }> => {
  const res = await fetch(`${API_CONFIG.baseUrl}/v2-caption`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({
      user_id: getUserId(),
      plan: opts.plan || undefined,
      video_title: opts.videoTitle || '',
      instruction: opts.instruction || '',
      current_caption: opts.currentCaption || '',
      want_title: !!opts.wantTitle,
      current_title: opts.currentTitle || '',
    }),
  });
  const data = (await res.json().catch(() => ({}))) as { caption?: string; title?: string; error?: string };
  if (isAuthRequiredError(res.status, data)) void endSessionForReauth();
  if (!res.ok) throw new Error(data.error || `v2-caption ${res.status}`);
  return { caption: data.caption || '', title: data.title };
};
